import { z } from "zod";

export enum AppModules {
  EMAIL_HEALTH = "email_health",
  MAILBOXES = "mailboxes",
  POSTMASTER = "postmaster",
  INBOX_PLACEMENT = "inbox_placement",
  SEED_LIST = "seed_list",
  TEMPLATES = "templates",
  LABELS = "labels",
  INTEGRATION = "integration",
  MANAGE_TEAM = "team",
  ROLES = "roles",
  BILLING = "billing",
  API_ACCESS = "api_access",
}

type PermissionKeys<T extends string> = {
  [key: string]: T;
};

export type ModulePermissions = {
  module: AppModules;
  permissions: PermissionKeys<string>;
};

export const PERMISSIONS = {
  MAILBOXES: {
    module: AppModules.MAILBOXES,
    permissions: {
      VIEW: "mailboxes:view",
      ADD: "mailboxes:add",
      EDIT: "mailboxes:edit",
      DELETE: "mailboxes:delete",
    },
  },

  POSTMASTER: {
    module: AppModules.POSTMASTER,
    permissions: {
      VIEW: "postmaster:view",
      ADD: "postmaster:add",
      EDIT: "postmaster:edit",
      DELETE: "postmaster:delete",
    },
  },
  EMAIL_HEALTH: {
    module: AppModules.EMAIL_HEALTH,
    permissions: {
      VIEW: "email_health:view",
      EDIT: "email_health:edit",
      RUN_TEST: "email_health:run_test",
      MANAGE_SCHEDULE: "email_health:manage_schedule",
    },
  },

  INBOX_PLACEMENT: {
    module: AppModules.INBOX_PLACEMENT,
    permissions: {
      VIEW: "inbox_placement:view",
      EDIT: "inbox_placement:edit",
      RUN_TEST: "inbox_placement:run_test",
      MANAGE_SCHEDULE: "inbox_placement:manage_schedule",
    },
  },

  SEED_LIST: {
    module: AppModules.SEED_LIST,
    permissions: {
      VIEW: "seed_list:view",
    },
  },

  TEMPLATES: {
    module: AppModules.TEMPLATES,
    permissions: {
      VIEW: "templates:view",
      CREATE: "templates:create",
      EDIT: "templates:edit",
      DELETE: "templates:delete",
    },
  },

  LABELS: {
    module: AppModules.LABELS,
    permissions: {
      VIEW: "labels:view",
      CREATE: "labels:create",
      EDIT: "labels:edit",
      DELETE: "labels:delete",
    },
  },

  INTEGRATION: {
    module: AppModules.INTEGRATION,
    permissions: {
      VIEW: "integration:view",
      ADD: "integration:add",
      REMOVE: "integration:remove",
    },
  },

  MANAGE_TEAM: {
    module: AppModules.MANAGE_TEAM,
    permissions: {
      VIEW: "team:view",
      EDIT: "team:edit",
      // INVITE: "team:invite", // only owner can invite
      REMOVE: "team:remove",
    },
  },

  ROLES: {
    module: AppModules.ROLES,
    permissions: {
      VIEW: "roles:view",
      CREATE: "roles:create",
      EDIT: "roles:edit",
      DELETE: "roles:delete",
    },
  },

  API_ACCESS: {
    module: AppModules.API_ACCESS,
    permissions: {
      VIEW: "api_access:view",
      CREATE: "api_access:create",
      DELETE: "api_access:delete",
    },
  },

  BILLING: {
    module: AppModules.BILLING,
    permissions: {
      VIEW: "billing:view",
      // MANAGE_SUBSCRIPTION: "billing:manage_subscription",
      // VIEW_INVOICES: "billing:view_invoices",
      // UPDATE_PAYMENT: "billing:update_payment",
    },
  },
} as const;

// Extract the type of the PERMISSIONS object
export type PermissionsType = typeof PERMISSIONS;

// Extract the type of a specific module's permissions
export type EmailHealthPermissions =
  PermissionsType["EMAIL_HEALTH"]["permissions"];
export type TemplatesPermissions = PermissionsType["TEMPLATES"]["permissions"];
export type InboxPlacementPermissions =
  PermissionsType["INBOX_PLACEMENT"]["permissions"];
export type ManageTeamPermissions =
  PermissionsType["MANAGE_TEAM"]["permissions"];
export type SeedListPermissions = PermissionsType["SEED_LIST"]["permissions"];
export type MailboxesPermissions = PermissionsType["MAILBOXES"]["permissions"];
export type PostmasterPermissions =
  PermissionsType["POSTMASTER"]["permissions"];
export type IntegrationPermissions =
  PermissionsType["INTEGRATION"]["permissions"];
export type BillingPermissions = PermissionsType["BILLING"]["permissions"];
export type LabelsPermissions = PermissionsType["LABELS"]["permissions"];
export type RolesPermissions = PermissionsType["ROLES"]["permissions"];
export type ApiAccessPermissions = PermissionsType["API_ACCESS"]["permissions"];

export type PermissionValues = {
  [K in keyof PermissionsType]: PermissionsType[K]["permissions"][keyof PermissionsType[K]["permissions"]];
}[keyof PermissionsType];

// Create a Zod schema for a single permission value
const permissionSchema = z.custom<PermissionValues>((val) => {
  // Extract all valid permission values from the PERMISSIONS object
  const validPermissions = Object.values(PERMISSIONS).flatMap((module) =>
    Object.values(module.permissions)
  );

  // Check if the value is in the valid permissions list
  return validPermissions.includes(val as PermissionValues);
}, "Invalid permission value");

// Create a Zod schema for an array of permissions
export const permissionsArraySchema = z
  .array(permissionSchema)
  .min(1, "At least one permission is required");
